import { HereGeocodeItemType } from '@/api/apis/here-maps';
import { LocationLike } from '@/api/types';
import { CountryEntryView } from '@/api/apis/facilities';
import { getIsEuropeanRegion } from './serviceRegion.helpers';

export type WithExtendedLocation<T extends { location: LocationLike }> = T & {
  location: T['location'] & { stateName?: string; countryName?: string; serviceRegionId?: string };
};

type ExtendLocationOptions = {
  countriesByIso2?: Record<string, CountryEntryView>;
  resolveSubdivisionName?: (stateCode?: string | null, countryCode?: string | null) => string | undefined;
};

export const withExtendedLocation = <T extends { location: LocationLike }>(
  item: T,
  { countriesByIso2 = {}, resolveSubdivisionName }: ExtendLocationOptions = {},
): WithExtendedLocation<T> => {
  const { state, country: countryCode } = item.location;
  const countryObj = countryCode ? countriesByIso2[countryCode] : undefined;

  return {
    ...item,
    location: {
      ...item.location,
      stateName: resolveSubdivisionName?.(state, countryCode) ?? state,
      countryName: countryObj?.name ?? countryCode,
      serviceRegionId: countryObj?.serviceRegionId,
    },
  };
};

export const extractGeocodePoints = (
  geocode: HereGeocodeItemType | null,
): { bboxPoints?: number[]; centerPoints?: number[] } => {
  let bboxPoints: number[] | undefined;
  let centerPoints: number[] | undefined;

  if (geocode?.mapView) {
    bboxPoints = [geocode.mapView.west, geocode.mapView.south, geocode.mapView.east, geocode.mapView.north];
  }
  if (geocode?.position) {
    centerPoints = [geocode.position.lat, geocode.position.lng];
  }

  return { bboxPoints, centerPoints };
};

type LocationDisplayInfo = {
  city?: string | null;
  state?: string | null;
  country?: string | null;
  stateName?: string;
  countryName?: string;
  serviceRegionId?: string;
};

export const pickRegionName = (item: { location: LocationDisplayInfo } | null | undefined): string | undefined => {
  const location = item?.location;
  const countryDisplayName = location?.countryName || location?.country || undefined;
  const stateDisplayName = location?.stateName || location?.state || undefined;

  if (getIsEuropeanRegion(location?.serviceRegionId)) {
    return countryDisplayName;
  }

  return stateDisplayName || countryDisplayName;
};

export const pickLocationDisplayName = (item: { location: LocationDisplayInfo } | null | undefined): string => {
  const city = item?.location?.city;
  if (!city) return '';

  return [city, pickRegionName(item)].filter(Boolean).join(', ');
};
